import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  reportsApi,
  SafetyEvidenceCardPayload,
  SignedRecordJsonObject,
  SignedRecordJsonValue,
  SignedSafetyControlRecord,
} from '../../services/reportsApi';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import { useSiteTimeZone } from '../../hooks/useSiteTimeZone';

type EvidenceState = 'idle' | 'loading' | 'loaded' | 'error';
type ViewerState = {
  kind: 'snapshot' | 'video';
  label: string;
  url: string;
  loading?: boolean;
  error?: boolean;
} | null;

function asObject(value: SignedRecordJsonValue | undefined): SignedRecordJsonObject {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as SignedRecordJsonObject
    : {};
}

function metadataCards(record: SignedSafetyControlRecord): SignedRecordJsonObject[] {
  return (record.detail?.evidence?.cards || []).map((card) => asObject(card));
}

function text(
  primary: SafetyEvidenceCardPayload | undefined,
  fallback: SignedRecordJsonObject,
  key: keyof SafetyEvidenceCardPayload,
): string | null {
  const first = primary?.[key];
  if (typeof first === 'string' && first.trim()) return first.trim();
  const second = fallback[String(key)];
  return typeof second === 'string' && second.trim() ? second.trim() : null;
}

function displayTimestamp(value: string | null, timeZone?: string | null): string {
  if (!value) return 'Timestamp not retained';
  return formatSiteDateTime(value, timeZone) || value;
}

function unavailableMessage(record: SignedSafetyControlRecord): string {
  const reason = record.detail?.evidence?.availability_reason;
  if (reason === 'viewer_not_authorized' || reason === 'unauthorized' || reason === 'redacted') {
    return 'Signed media unavailable to this viewer';
  }
  if (reason === 'no_evidence_retained' || record.detail?.evidence?.total_items === 0) {
    return 'No signed snapshot retained';
  }
  return 'Signed snapshot not yet loaded';
}

export function EvidenceStrip({
  record,
  orgId,
  siteId,
  illustrativeSnapshots,
  illustrativeClipUrl,
}: {
  record: SignedSafetyControlRecord;
  orgId?: string | null;
  siteId?: string | null;
  /** Development fixture only. Never pass this prop from a live signed-record route. */
  illustrativeSnapshots?: string[];
  /** Development fixture only. Never pass this prop from a live signed-record route. */
  illustrativeClipUrl?: string;
}) {
  const timeZone = useSiteTimeZone(siteId);
  const illustrative = Array.isArray(illustrativeSnapshots);
  const illustrativeRecord = record.signed_body?.identity?.provenance === 'illustrative_demonstration'
    || record.record_provenance?.state === 'illustrative_demonstration'
    || (record.signed_body?.evidence || []).some((item) => item?.illustrative);
  const metadata = metadataCards(record);
  const signedEvidence = useMemo(
    () => record.signed_body?.evidence || [],
    [record.signed_body?.evidence],
  );
  const [state, setState] = useState<EvidenceState>('idle');
  const [hydrated, setHydrated] = useState<SafetyEvidenceCardPayload[]>([]);
  const [loadedReason, setLoadedReason] = useState<string | undefined>();
  const [snapshotUrls, setSnapshotUrls] = useState<Record<number, string>>({});
  const [snapshotFailures, setSnapshotFailures] = useState<Record<number, boolean>>({});
  const [viewer, setViewer] = useState<ViewerState>(null);
  const viewerRequest = useRef(0);
  const autoLoadStarted = useRef(false);
  const hasEvidenceSlots = metadata.length > 0
    || signedEvidence.length > 0
    || (record.detail?.evidence?.total_items || 0) > 0;

  const load = async () => {
    if (illustrative) return;
    setState('loading');
    try {
      const response = await reportsApi.getSignedSafetyControlRecordEvidence(
        record.record_id,
        record.revision,
        { orgId, siteId },
      );
      if (!response.ok || !response.evidence) throw new Error('evidence unavailable');
      setHydrated(response.evidence.cards || []);
      setLoadedReason(response.evidence.availability_reason);
      setState('loaded');
    } catch {
      setHydrated([]);
      setLoadedReason(undefined);
      setState('error');
    }
  };

  useEffect(() => {
    if (
      illustrative
      || autoLoadStarted.current
      || record.detail?.evidence?.availability_reason === 'viewer_not_authorized'
      || !hasEvidenceSlots
    ) {
      return;
    }
    autoLoadStarted.current = true;
    void load();
    // Auto-load once whenever signed evidence slots exist so real stills appear without a click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [illustrative, hasEvidenceSlots, record.record_id, record.revision]);

  useEffect(() => {
    if (illustrative) return undefined;
    const uris = [0, 1, 2].map((index) => {
      const card = hydrated[index];
      const signed = signedEvidence[index];
      return String(
        card?.snapshot_uri
        || card?.media_uri
        || signed?.snapshot_ref
        || '',
      ).trim();
    });
    if (!uris.some(Boolean)) return undefined;
    let cancelled = false;
    const objectUrls: string[] = [];
    setSnapshotUrls({});
    setSnapshotFailures({});

    uris.forEach((uri, index) => {
      if (!uri) return;
      reportsApi.fetchSignedSafetyControlRecordEvidenceMedia(uri).then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        setSnapshotUrls((current) => ({ ...current, [index]: url }));
      }).catch(() => {
        if (!cancelled) {
          setSnapshotFailures((current) => ({ ...current, [index]: true }));
        }
      });
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [hydrated, illustrative, signedEvidence]);

  const slots = useMemo(() => [0, 1, 2].map((index) => {
    const meta = metadata[index] || {};
    const card = hydrated[index];
    const signed = signedEvidence[index];
    const id = String(
      card?.evidence_id
      || signed?.evidence_id
      || meta.customer_evidence_ref
      || meta.evidence_id
      || `EV-${String(index + 1).padStart(3, '0')}`,
    );
    const caption = text(card, meta, 'caption')
      || text(card, meta, 'safety_rule')
      || text(card, meta, 'area')
      || (typeof signed?.title === 'string' ? signed.title : null)
      || 'Representative signed evidence';
    const camera = text(card, meta, 'camera_id');
    const zone = text(card, meta, 'zone_id') || text(card, meta, 'area');
    const review = text(card, meta, 'review_status')
      || text(card, meta, 'source_status')
      || 'Review label not retained';
    return {
      id,
      caption,
      cameraZone: [
        camera
          ? (/^camera\b/i.test(camera) ? camera : `Camera ${camera}`)
          : null,
        zone
          ? (/^zone\b/i.test(zone) ? zone : `Zone ${zone}`)
          : null,
      ].filter(Boolean).join(' · ') || 'Camera / zone not retained',
      timestamp: displayTimestamp(text(card, meta, 'timestamp'), timeZone),
      review,
      media: illustrative
        ? illustrativeSnapshots?.[index] || null
        : snapshotUrls[index] || card?.rendered_image_data_url || null,
      clip: illustrative && index === 0
        ? illustrativeClipUrl || null
        : String(
          card?.clip_path
          || card?.secondary_clip_uri
          || signed?.clip_ref
          || '',
        ).trim() || null,
      failed: Boolean(snapshotFailures[index]),
    };
  }), [
    hydrated,
    illustrative,
    illustrativeClipUrl,
    illustrativeSnapshots,
    metadata,
    signedEvidence,
    snapshotFailures,
    snapshotUrls,
    timeZone,
  ]);

  const loadedUnavailable = state === 'loaded' && hydrated.length === 0
    ? (loadedReason === 'viewer_not_authorized'
      ? 'Signed media unavailable to this viewer'
      : 'No hydrated signed snapshots are available')
    : null;

  const openSnapshot = (label: string, url: string) => {
    setViewer({ kind: 'snapshot', label, url });
  };

  const playClip = async (label: string, uri: string) => {
    const request = viewerRequest.current + 1;
    viewerRequest.current = request;
    if (illustrative) {
      setViewer({ kind: 'video', label, url: uri });
      return;
    }
    setViewer({ kind: 'video', label, url: '', loading: true });
    try {
      const blob = await reportsApi.fetchSignedSafetyControlRecordEvidenceMedia(uri);
      if (viewerRequest.current !== request) return;
      setViewer({ kind: 'video', label, url: URL.createObjectURL(blob) });
    } catch {
      if (viewerRequest.current !== request) return;
      setViewer({ kind: 'video', label, url: '', error: true });
    }
  };

  const closeViewer = () => {
    viewerRequest.current += 1;
    if (viewer?.url.startsWith('blob:')) URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  return (
    <Box component="section" aria-labelledby="evidence-strip-heading" data-testid="evidence-strip">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: '#087fa5', letterSpacing: '0.16em', fontWeight: 800 }}
          >
            Signed evidence
          </Typography>
          <Typography
            id="evidence-strip-heading"
            component="h2"
            sx={{ mt: 0.4, fontSize: { xs: '1.65rem', md: '2.2rem' }, fontWeight: 800 }}
          >
            Snapshots and clips anchor the review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {illustrative || illustrativeRecord
              ? 'Illustrative demonstration frames — not customer-site evidence.'
              : `Media is hydrated only from signed revision ${record.revision}.`}
          </Typography>
        </Box>
        {!illustrative
          && state === 'idle'
          && record.detail?.evidence?.availability_reason !== 'viewer_not_authorized'
          && hasEvidenceSlots ? (
          <Button variant="outlined" onClick={() => void load()}>
            Load signed evidence
          </Button>
        ) : null}
      </Stack>

      {state === 'error' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Signed media could not be loaded. Metadata and the signed record remain available.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 1.5 },
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {slots.map((slot, index) => (
          <Box
            component="figure"
            key={slot.id}
            data-testid={`evidence-slot-${index + 1}`}
            sx={{ m: 0, minWidth: 0 }}
          >
            <Box
              sx={{
                position: 'relative',
                aspectRatio: '16 / 10',
                overflow: 'hidden',
                bgcolor: '#dce6ed',
                borderTop: '3px solid #18bde3',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {slot.media && !slot.failed ? (
                <Box
                  component="img"
                  src={slot.media}
                  alt={`${illustrative ? 'Illustrative' : 'Signed'} evidence ${slot.id}`}
                  onError={() => setSnapshotFailures((current) => ({ ...current, [index]: true }))}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ px: 2, maxWidth: 210 }}
                >
                  {state === 'loading'
                    ? 'Loading signed evidence…'
                    : slot.failed
                      ? 'Signed snapshot unavailable'
                    : loadedUnavailable || unavailableMessage(record)}
                </Typography>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  px: 1,
                  py: 0.4,
                  bgcolor: '#081b33',
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {slot.id}
              </Box>
              {illustrative || illustrativeRecord ? (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 8,
                    bottom: 8,
                    px: 0.8,
                    py: 0.35,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: '#081b33',
                    fontSize: 11,
                    fontWeight: 900,
                    textTransform: 'uppercase',
                  }}
                >
                  Illustrative
                </Box>
              ) : null}
            </Box>
            <Box component="figcaption" sx={{ pt: 1.15 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#087fa5' }}>
                {slot.id}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700 }}>
                {slot.caption}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                {slot.timestamp}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {slot.cameraZone}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {slot.review}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.1 }} useFlexGap flexWrap="wrap">
                {slot.media && !slot.failed ? (
                  <Button size="small" onClick={() => openSnapshot(slot.id, slot.media as string)}>
                    Open snapshot
                  </Button>
                ) : null}
                {slot.clip ? (
                  <Button size="small" variant="outlined" onClick={() => void playClip(slot.id, slot.clip as string)}>
                    Play clip
                  </Button>
                ) : null}
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog
        open={viewer != null}
        onClose={closeViewer}
        maxWidth="md"
        fullWidth
        aria-labelledby="signed-evidence-viewer-title"
      >
        <DialogTitle id="signed-evidence-viewer-title">
          {viewer?.label} · {illustrative
            ? `Illustrative ${viewer?.kind === 'video' ? 'clip' : 'snapshot'}`
            : `Signed ${viewer?.kind === 'video' ? 'clip' : 'snapshot'}`}
        </DialogTitle>
        <DialogContent>
          {viewer?.loading ? (
            <Typography role="status">Loading signed clip…</Typography>
          ) : viewer?.error ? (
            <Alert severity="warning">Signed clip unavailable to this viewer.</Alert>
          ) : viewer?.kind === 'video' && viewer.url ? (
            <Box
              component="video"
              src={viewer.url}
              controls
              aria-label={`${viewer.label} ${illustrative ? 'illustrative' : 'signed evidence'} clip`}
              sx={{ display: 'block', width: '100%', maxHeight: '70vh', bgcolor: '#081b33' }}
            />
          ) : viewer?.kind === 'snapshot' && viewer.url ? (
            <Box
              component="img"
              src={viewer.url}
              alt={`${viewer.label} signed evidence snapshot`}
              sx={{ display: 'block', width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
